import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import FeedbackForm from './components/FeedbackForm';

const SERVICES = [
  {
    icon: 'ri-film-line',
    title: '影视内容聚合',
    desc: '聚合电影、电视剧与综艺节目信息，覆盖科幻、悬疑、都市、美食等多元题材，一站式浏览。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '智能筛选搜索',
    desc: '支持按类型、地区、年份与热度筛选，并提供关键词模糊搜索，帮助你在海量内容中快速定位。',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: '资讯与服务',
    desc: '持续更新最新影视动态，提供清晰的片单推荐与内容介绍，让你随时了解值得一看的影视作品。',
  },
];

const CONTACTS = [
  { icon: 'ri-mail-line', label: '商务与合作', value: 'hello@siguavideo.com' },
  { icon: 'ri-customer-service-2-line', label: '客服热线', value: '400-800-2025' },
  { icon: 'ri-map-pin-line', label: '办公地址', value: '上海市浦东新区世纪大道 100 号' },
];

export default function About() {
  useDocumentMeta({
    title: '关于我们 - 丝瓜视频',
    description:
      '了解丝瓜视频的网站定位、服务范围与联系方式。丝瓜视频是面向影视爱好者的内容聚合平台，聚合电影、电视剧与综艺节目信息。',
    keywords: '丝瓜视频,关于我们,影视聚合,视频平台介绍,联系方式',
  });

  return (
    <SiteLayout>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src="https://readdy.ai/api/search-image?query=cinematic%20dark%20green%20abstract%20gradient%20background%20with%20soft%20light%20rays%20and%20subtle%20film%20grain%2C%20minimal%20elegant%2C%20no%20text&width=1600&height=600&seq=sgv-about&orientation=landscape"
          alt="丝瓜视频 关于我们"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/50" />
        <div className="relative mx-auto w-full max-w-[1360px] px-4 py-16 md:px-6 md:py-24">
          <nav className="mb-6 flex items-center gap-1.5 text-sm text-primary-100">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-background-50">关于我们</span>
          </nav>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/90 px-3 py-1 font-label text-xs font-semibold text-background-50">
            <i className="ri-leaf-line" /> 关于丝瓜视频
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            让每一次打开，都能遇见值得一看的好内容
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-primary-100 md:text-base">
            丝瓜视频聚焦优质影视资源聚合，致力于为影视爱好者、追剧党与综艺迷提供清晰、流畅的内容浏览体验。我们用分类与搜索连接你与好内容，让发现影视变得简单而愉快。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1360px] px-4 py-10 md:px-6 md:py-16">
        <section>
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们在做什么</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            丝瓜视频是一个影视内容聚合与资讯平台。我们持续整理电影、电视剧及综艺节目的基础信息与简介，通过类型分类、多维筛选与关键词搜索，帮助用户高效浏览、筛选并了解感兴趣的作品。本站不提供真实视频播放服务，所有影视内容均为演示数据。
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {SERVICES.map((s) => (
              <div key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${s.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-3 md:mt-16">
          <div className="lg:col-span-1">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              无论是内容建议、资源反馈还是商务合作，都欢迎通过以下方式与我们取得联系。
            </p>
            <ul className="mt-6 space-y-4">
              {CONTACTS.map((c) => (
                <li key={c.label} className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                    <i className={`${c.icon} text-lg`} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{c.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h2 className="mb-4 font-heading text-2xl font-bold text-foreground-950">意见反馈</h2>
            <FeedbackForm />
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 gap-4 md:mt-16 md:grid-cols-2 md:gap-5">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-copyright-line text-primary-600" /> 版权声明
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站所有文字、图表与页面设计均由丝瓜视频整理或创作，仅供展示与学习交流使用。站内影视海报、片名及简介均为虚拟示范数据，如与真实作品存在雷同，纯属巧合。未经许可，请勿用于商业用途。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-shield-check-line text-accent-600" /> 免责声明
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              丝瓜视频为影视信息聚合演示平台，不存储、不提供任何真实视频文件的播放与下载。播放区域仅为前端交互演示，不代表真实播放能力。若本站内容无意侵犯到相关权益，请联系我们，我们将及时处理。
            </p>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}